import React from 'react';
import {
  Autocomplete,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  MenuItem,
  CircularProgress,
  Typography,
  FormHelperText,
  Box,
} from '@mui/material';
import { FormField, FormRendererProps, FormRenderTypes } from './FormRenderer.types';
import { TinyMCEConfigs } from '../TinyMCE/tinyMCEConfig';
import { Editor } from '@tinymce/tinymce-react';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { resolveAdminApiUrl } from '../../../../Api/apiUrl';
import dayjs, { Dayjs } from 'dayjs';
import DynamicListField from '../DynamicListField/DynamicListField';
import { OfferOptionsField } from '../OfferOptionsField/OfferOptionsField';

dayjs.locale('pl');
type TinyMceBlobInfo = {
  blob: () => Blob;
  filename: () => string;
};

type ImagesUploadHandler = (
  blobInfo: TinyMceBlobInfo,
  success: (url: string) => void,
  failure: (errMsg: string) => void
) => void;

const ADMIN_API_URL = resolveAdminApiUrl(process.env.REACT_APP_API_URL);

const imagesUploadHandler: ImagesUploadHandler = async (blobInfo, success, failure) => {
  const formData = new FormData();
  formData.append('file', blobInfo.blob(), blobInfo.filename());
  console.log('Uploading image:', blobInfo.filename());
  try {
    const res = await fetch(`${ADMIN_API_URL}/images/upload-tinymce`, {
      method: 'POST',
      body: formData,
    });
    const data = await res.json();
    console.log('UPLOAD:', data, typeof data.location);
    if (typeof data.location === 'string' && data.location.trim() !== '') {
      console.log('Przekazuję do success:', data.location, typeof data.location);

      success(data.location);
    } else {
      if (typeof failure === 'function') {
        failure('Nieprawidłowy URL obrazka z serwera');
      }
    }
  } catch (err) {
    if (typeof failure === 'function') {
      failure(
        'Błąd uploadu: ' +
          (typeof err === 'object' && err !== null && 'message' in err
            ? (err as { message?: string }).message
            : String(err))
      );
    }
  }
};

interface AutocompleteFormFieldProps {
  field: FormField;
  onChange: FormRendererProps['onChange'];
  customFilterOptions?: FormRendererProps['customFilterOptions'];
  autocompleteInputChangeHandlers?: FormRendererProps['autocompleteInputChangeHandlers'];
  resetKey?: number;
}

const AutocompleteFormField = ({
  field,
  onChange,
  customFilterOptions,
  autocompleteInputChangeHandlers,
}: AutocompleteFormFieldProps) => {
  const opts = field.options ?? [];
  const fieldName = String(field.name);
  const equalsValue = (a: unknown, b: unknown) => String(a) === String(b);

  const multipleValueObjs = opts.filter((o) =>
    Array.isArray(field.value)
      ? (field.value as any[]).some((v) => equalsValue(v, o.value))
      : false
  );
  const singleValueObj = opts.find((o) => equalsValue(o.value, field.value)) ?? null;
  const valueObjs = field.multiple ? multipleValueObjs : singleValueObj;

  const [inputValue, setInputValue] = React.useState(() =>
    field.multiple ? '' : (singleValueObj?.label ?? '')
  );

  // Sync inputValue when field.value is set/cleared externally (e.g., after modal creation)
  // Deps: field.value (value changed) + singleValueObj?.label (options arrived after async load)
  React.useEffect(() => {
    if (field.multiple) return;
    if (!field.value && field.value !== 0) {
      setInputValue('');
      return;
    }
    if (singleValueObj) {
      setInputValue(singleValueObj.label);
    }
    // value set but options not yet loaded → don't override current inputValue
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value, singleValueObj?.label]);

  return (
    <FormControl fullWidth margin="normal">
      <Autocomplete
        multiple={field.multiple}
        options={opts}
        getOptionKey={(option) => String(option.value)}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(opt, val) => equalsValue(opt.value, val.value)}
        loading={field.loading}
        noOptionsText={
          inputValue.trim().length < 3 ? 'Wpisz co najmniej 3 znaki' : 'Brak wyników'
        }
        value={valueObjs}
        inputValue={inputValue}
        onOpen={() => {
          autocompleteInputChangeHandlers?.[fieldName]?.('', 'open');
        }}
        onInputChange={(_, newInputValue, reason) => {
          const isDirectInputChange = reason === 'input' || reason === 'clear';

          if (isDirectInputChange) {
            setInputValue(newInputValue);
          }

          if (!autocompleteInputChangeHandlers) {
            return;
          }

          if (!isDirectInputChange) {
            return;
          }

          const mappedReason = reason === 'clear' ? 'clear' : 'input';
          autocompleteInputChangeHandlers[fieldName]?.(newInputValue, mappedReason);
        }}
        filterOptions={
          customFilterOptions && customFilterOptions[fieldName]
            ? (options, state) => customFilterOptions[fieldName](options, state)
            : undefined
        }
        onChange={(_, newVal) => {
          const newValue = field.multiple
            ? (newVal as any[]).map((o) => o.value)
            : ((newVal as any)?.value ?? null);
          const isAddNewSelection =
            !field.multiple && (newVal as { value?: unknown } | null)?.value === '__new__';

          if (!field.multiple) {
            // Keep input clean after selecting synthetic "add new" option.
            setInputValue(isAddNewSelection ? '' : ((newVal as any)?.label ?? ''));
          } else {
            setInputValue('');
          }

          onChange({
            target: { name: field.name, value: newValue },
          } as React.ChangeEvent<HTMLInputElement>);
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;

          return (
            <li key={key} {...optionProps}>
              <Box>
                <Typography variant="body2">{option.label}</Typography>
                {option.description && (
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                )}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={field.label}
            placeholder={field.placeholder}
            helperText={field.helperText}
            error={field.error}
            variant="outlined"
            sx={{ mt: 2 }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {field.loading && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </FormControl>
  );
};

const FormRenderer = ({
  fields,
  onChange,
  customFilterOptions,
  autocompleteInputChangeHandlers,
  autocompleteResetKeys,
}: FormRendererProps) => {
  return (
    <>
      {fields.map((field) => {
        if (field.hidden) {
          return null;
        }
        switch (field.type) {
          // Standardowy input tekstowy
          case FormRenderTypes.input:
            return (
              <TextField
                key={String(field.name)}
                name={String(field.name)}
                fullWidth
                label={field.label}
                placeholder={field.placeholder}
                error={field.error}
                helperText={field.helperText}
                value={field.value ?? ''}
                onChange={onChange}
                sx={{ mt: 2 }}
              />
            );

          // Input numeryczny
          case FormRenderTypes.number:
            return (
              <TextField
                key={String(field.name)}
                name={String(field.name)}
                type="number"
                fullWidth
                label={field.label}
                placeholder={field.placeholder}
                error={field.error}
                helperText={field.helperText}
                value={field.value ?? ''}
                onChange={onChange}
                disabled={field.disabled}
                inputProps={field.inputProps}
                slotProps={{
                  htmlInput: {
                    readOnly: field.readonly,
                  },
                }}
                sx={{
                  mt: 2,
                  ...(field.readonly && {
                    '& .MuiInputBase-input': {
                      cursor: 'not-allowed',
                      backgroundColor: 'action.hover',
                    },
                  }),
                }}
              />
            );

          // Input email
          case FormRenderTypes.email:
            return (
              <TextField
                key={String(field.name)}
                name={String(field.name)}
                type="email"
                fullWidth
                label={field.label}
                placeholder={field.placeholder}
                error={field.error}
                helperText={field.helperText}
                value={field.value ?? ''}
                onChange={onChange}
                sx={{ mt: 2 }}
              />
            );

          // Input daty – etykieta z shrink, żeby nie nakładała się na pole
          case FormRenderTypes.date:
            return (
              <LocalizationProvider
                key={String(field.name)}
                dateAdapter={AdapterDayjs}
                adapterLocale="pl" // for Polish locale
              >
                <DatePicker
                  label={field.label}
                  // parse the incoming ISO‐string (or empty) into Dayjs | null
                  value={field.value ? dayjs(field.value as string) : null}
                  // whenever it changes, emit back "YYYY-MM-DD"
                  onChange={(newVal: Dayjs | null) => {
                    const value = newVal ? newVal.format('YYYY-MM-DD') : '';
                    onChange({
                      target: {
                        name: field.name,
                        value,
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  format="YYYY-MM-DD"
                  disabled={field.disabled}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: field.error,
                      helperText: field.helperText,
                      sx: { mt: 2 },
                    },
                  }}
                />
              </LocalizationProvider>
            );

          //Pole textarea (wieloliniowe pole tekstowe)
          case FormRenderTypes.textarea:
            return (
              <TextField
                key={String(field.name)}
                name={String(field.name)}
                fullWidth
                label={field.label}
                placeholder={field.placeholder}
                error={field.error}
                helperText={field.helperText}
                value={field.value ?? ''}
                onChange={onChange}
                multiline
                rows={field.rows || 4}
                sx={{ mt: 2 }}
              />
            );
          case FormRenderTypes.wysiwyg:
            return (
              <FormControl fullWidth margin="normal" key={String(field.name)}>
                <Typography variant="subtitle1" gutterBottom>
                  {field.label}
                </Typography>
                <Editor
                  licenseKey="gpl"
                  tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
                  apiKey="hl8vxlvwl4bzp1mcvxs7a4199tjukwpgx6tzzzztpzwth5ir"
                  value={field.value ?? ''}
                  init={TinyMCEConfigs.form('ltr', imagesUploadHandler)}
                  onEditorChange={(content) =>
                    onChange({
                      target: {
                        name: field.name,
                        value: content,
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                />
              </FormControl>
            );
          // Pole select – spodziewamy się, że w polu field.options podana jest tablica obiektów { value, label }
          case FormRenderTypes.select:
            return (
              <TextField
                key={String(field.name)}
                name={String(field.name)}
                select
                fullWidth
                label={field.label}
                error={field.error}
                helperText={field.helperText}
                value={field.value ?? ''}
                onChange={onChange}
                sx={{ mt: 2, ...(field.sx || {}) }}
              >
                {field.options && field.options.length > 0 ? (
                  field.options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    Brak opcji
                  </MenuItem>
                )}
              </TextField>
            );

          // Grupa przycisków radiowych – również oparta na field.options
          case FormRenderTypes.radio:
            return (
                <FormControl component="fieldset" key={String(field.name)} margin="normal" error={field.error}>
                <FormLabel component="legend">{field.label}</FormLabel>
                <RadioGroup name={String(field.name)} value={field.value ?? ''} onChange={onChange}>
                  {field.options &&
                    field.options.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                      />
                    ))}
                </RadioGroup>
                {field.helperText && <FormHelperText>{field.helperText}</FormHelperText>}
              </FormControl>
            );

          // Checkbox – pojedynczy element sterowany wartością boolean
          case FormRenderTypes.checkbox:
            return (
              <FormControlLabel
                key={String(field.name)}
                control={
                  <Checkbox
                    inputProps={{ name: String(field.name) }}
                    // Jeśli wartość może być stringiem ("true"/"false"), porównujemy ją,
                    // w przeciwnym przypadku domyślnie wymuszamy boolean:
                    checked={
                      typeof field.value === 'string'
                        ? field.value === 'true'
                        : Boolean(field.value)
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onChange({
                        target: {
                          ...e.target,
                          name: field.name,
                          value: e.target.checked,
                        },
                      } as unknown as React.ChangeEvent<HTMLInputElement>)
                    }
                  />
                }
                label={field.label}
              />
            );

          // Switch – przedstawienie kontrolki jako przełącznika
          case FormRenderTypes.switch:
            return (
              <FormControlLabel
                key={String(field.name)}
                control={
                  <Switch
                    slotProps={{
                      input: {
                        name: String(field.name),
                      },
                    }}
                    checked={
                      typeof field.value === 'string'
                        ? field.value === 'true'
                        : Boolean(field.value)
                    }
                    onChange={(_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                      const customEvent = {
                        target: {
                          name: field.name,
                          value: checked,
                        },
                      };
                      onChange(customEvent as unknown as React.ChangeEvent<HTMLInputElement>);
                    }}
                  />
                }
                label={field.label}
              />
            );

          // Input do wyboru plików – wartość dostępna w e.target.files
          case FormRenderTypes.file:
            return (
              <TextField
                key={String(field.name)}
                name={String(field.name)}
                type="file"
                fullWidth
                label={field.label}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const customEvent = {
                    target: {
                      name: field.name,
                      value: (e.target as HTMLInputElement).files,
                    },
                  };
                  onChange(customEvent as unknown as React.ChangeEvent<HTMLInputElement>);
                }}
                sx={{ mt: 2 }}
              />
            );
          case FormRenderTypes.autocomplete:
            return (
              <AutocompleteFormField
                key={`${String(field.name)}-${autocompleteResetKeys?.[String(field.name)] ?? 0}`}
                field={field}
                onChange={onChange}
                customFilterOptions={customFilterOptions}
                autocompleteInputChangeHandlers={autocompleteInputChangeHandlers}
              />
            );
          case FormRenderTypes.dynamicList:
            return (
              <DynamicListField
                key={String(field.name)}
                value={field.value || []}
                onChange={(val) => {
                  onChange({
                    target: {
                      name: field.name,
                      value: val,
                    },
                  } as unknown as React.ChangeEvent<HTMLInputElement>);
                }}
                itemFields={(field.itemFields || []).map(({ name, label, type, sx }) => ({
                  name: String(name),
                  label,
                  // Map "input" to "text" to match DynamicListFieldItemField type
                  type:
                    type === 'input'
                      ? 'text'
                      : type === 'number' || type === 'select'
                      ? type
                      : undefined,
                  sx,
                }))}
                label={field.label}
              />
            );
          case FormRenderTypes.offerOptions:
            return (
              <OfferOptionsField
                key={String(field.name)}
                value={field.value || []}
                onChange={(newValue) => {
                  onChange({
                    target: {
                      name: field.name,
                      value: newValue,
                    },
                  } as unknown as React.ChangeEvent<HTMLInputElement>);
                }}
                optionTypes={field.optionTypes || []} // przekazujesz pobrane optionTypes (np. z API)
                loading={field.loading}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};

export default FormRenderer;
